import React, { useCallback } from 'react';
import useSystemTelemetry from '../hooks/useSystemTelemetry';
import useDragRotate from '../hooks/useDragRotate';
import CoinBack from './CoinBack';
import { sideFromAngle } from '../utils/coinSide';

/**
 * Minimized "standby mode" view: big animated logo + idle status panel.
 * Shows REAL browser telemetry (heap memory, network downlink, FPS) and
 * session metrics (uptime, command count) of the visitor.
 */
const MinimizedStandby = ({
  logoSrc,
  name = 'C4sh3r',
  onResume,
  sessionStart,
  commandCount = 0
}) => {
  const { stats, uptime, localClock } = useSystemTelemetry(sessionStart);
  const { angle, isDragging, isAnimating, handlers, wasDragged, flip } = useDragRotate({ sensitivity: 0.55 });

  const handleLogoClick = useCallback(() => {
    if (wasDragged()) return;
    flip(Math.random() > 0.5 ? 1 : -1, 1); // 1 extra spin for the big standby coin
  }, [flip, wasDragged]);

  const memSupported = !!(performance && performance.memory);
  const netSupported = !!(navigator.connection || navigator.mozConnection || navigator.webkitConnection);

  const logLines = [
    `> session.uptime ......... ${uptime}`,
    `> commands.executed ...... ${String(commandCount).padStart(3, '0')}`,
    '> heartbeat .............. ok',
    '> firewall ............... armed',
    '> uplink ................. secure'
  ];

  return (
    <div className="standby" data-testid="terminal-standby">
      <div className="standby-grid" aria-hidden="true" />
      <div className="standby-scanline" aria-hidden="true" />

      <div className="standby-core">
        <div className="standby-logo-wrap">
          <span className="standby-ring standby-ring-1" />
          <span className="standby-ring standby-ring-2" />
          <span className="standby-ring standby-ring-3" />
          <span className="standby-pulse" />
          {logoSrc && (
            <button
              type="button"
              className={`standby-logo-btn coin-stage${isDragging ? ' is-dragging' : ''}`}
              onClick={handleLogoClick}
              aria-label="Flip C4sh3r logo"
              title="Click para girar / arrastra para rotar"
              data-testid="standby-logo-btn"
              {...handlers}
            >
              <span
                className={`coin coin-lg${isAnimating ? ' is-spinning' : ''}${isDragging ? ' is-dragging' : ''}`}
                style={{ transform: `rotateY(${angle}deg)` }}
                data-side={sideFromAngle(angle)}
                aria-hidden="true"
              >
                <span className="coin-face coin-front">
                  <img
                    src={logoSrc}
                    alt={`${name} standby`}
                    className="standby-logo"
                    data-testid="standby-logo"
                    draggable={false}
                  />
                </span>
                <span className="coin-face coin-back">
                  <CoinBack size="lg" qrValue="https://www.linkedin.com/in/c4sh3r/" />
                </span>
              </span>
            </button>
          )}
        </div>

        <div className="standby-title" data-testid="standby-title">
          <span className="standby-bracket">[</span>
          <span className="standby-title-text">STANDBY MODE</span>
          <span className="standby-bracket">]</span>
        </div>

        <div className="standby-subtitle">
          {name.toUpperCase()} :: SYSTEM IDLE — <span className="standby-clock">{localClock}</span>
        </div>

        <button
          type="button"
          className="standby-resume"
          onClick={onResume}
          data-testid="standby-resume-btn"
        >
          <span className="standby-resume-icon">▶</span>
          <span>Reanudar sesión</span>
        </button>
      </div>

      <div className="standby-side standby-side-left" aria-hidden="true">
        <div className="standby-panel-title">// session.log</div>
        {logLines.map((line, i) => (
          <div key={i} className="standby-log-line" style={{ animationDelay: `${i * 0.18}s` }}>
            {line}
          </div>
        ))}
      </div>

      <div className="standby-side standby-side-right">
        <div className="standby-panel-title">// telemetry · live</div>
        {[
          {
            label: 'MEM',
            value: stats.mem,
            color: 'var(--cyan-primary)',
            note: memSupported ? null : 'n/a',
            unit: '%'
          },
          {
            label: 'NET',
            value: stats.net,
            color: '#9fef00',
            note: netSupported ? null : 'n/a',
            unit: '%'
          },
          {
            label: 'FPS',
            value: Math.round((stats.fps / 60) * 100),
            color: '#ffbd2e',
            displayValue: stats.fps,
            unit: ''
          }
        ].map(({ label, value, color, note, displayValue, unit }) => (
          <div key={label} className="standby-stat">
            <span className="standby-stat-label">{label}</span>
            <div className="standby-bar">
              <div
                className="standby-bar-fill"
                style={{
                  width: `${Math.min(100, Math.max(0, value))}%`,
                  background: color,
                  boxShadow: `0 0 8px ${color}`
                }}
              />
            </div>
            <span className="standby-stat-value" style={{ color }}>
              {note ? note : `${displayValue !== undefined ? displayValue : value}${unit}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MinimizedStandby;
